/**
 * Ephemeral frame-rate sampler for the diagnostics view (Story 1.6, FR-18). Frame
 * rate is an INSTRUMENT READING, not command-driven state: it MUST NOT dispatch a
 * command per frame (that would churn the store ~60x/sec and violate AD-2), it is
 * never persisted and never shared (AD-4/AD-7). It is exposed as a local reactive
 * value the diagnostics component reads for display only.
 *
 * The scene is static/paused in the committed baseline, so ordinary renders do not
 * happen on their own; this sampler runs its OWN requestAnimationFrame loop to
 * measure the frame interval and report a smoothed frames-per-second value. It is
 * started and stopped with the diagnostics view lifecycle to avoid an always-on loop.
 */

export interface FpsSampler {
  /** Stop the sampling loop and release the frame request. */
  stop(): void;
}

interface FpsSamplerOptions {
  /** Injectable clock for testing; defaults to `performance.now` in ms. */
  now?: () => number;
  /** Injectable rAF scheduler; defaults to `requestAnimationFrame`. */
  requestFrame?: (cb: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
  /** Exponential smoothing factor in [0, 1]; higher reacts faster. */
  smoothing?: number;
}

/**
 * Start sampling. Calls `onSample` with a smoothed FPS value roughly once per
 * frame. Returns a handle whose `stop()` ends the loop.
 */
export function startFpsSampler(
  onSample: (fps: number) => void,
  options: FpsSamplerOptions = {}
): FpsSampler {
  const now = options.now ?? (() => performance.now());
  const requestFrame =
    options.requestFrame ?? ((cb: FrameRequestCallback) => requestAnimationFrame(cb));
  const cancelFrame = options.cancelFrame ?? ((h: number) => cancelAnimationFrame(h));
  const smoothing = options.smoothing ?? 0.1;

  let handle = 0;
  let running = true;
  let last = now();
  let smoothed = 0;

  const frame = (): void => {
    if (!running) return;
    const current = now();
    const deltaMs = current - last;
    last = current;
    if (deltaMs > 0) {
      const instant = 1000 / deltaMs;
      smoothed = smoothed === 0 ? instant : smoothed + smoothing * (instant - smoothed);
      onSample(Math.round(smoothed));
    }
    handle = requestFrame(frame);
  };

  handle = requestFrame(frame);

  return {
    stop() {
      running = false;
      cancelFrame(handle);
    }
  };
}
